// The page's space: opened once, kept across screens so its sync keeps running.

import { Space } from './space';
import { SpaceSync } from './sync';
import { Conversations } from '../contacts/conversations';
import { iceServers, wsUrl } from '../ui/common';

let current: Promise<Space | null> | null = null;
let conversations: Promise<Conversations | null> | null = null;

function started(space: Space | null): Space | null {
  space?.startSync(wsUrl, iceServers);
  // End-to-end tests (which inject __dropMeshes before load) reach the live space through this.
  const g = globalThis as { __dropMeshes?: unknown; __space?: Space | null };
  if (g.__dropMeshes) g.__space = space;
  return space;
}

export function getSpace(): Promise<Space | null> {
  current ??= Space.open().then((space) => {
    if (space) requestPersistence(); // Safari forgets the grant: ask at every launch
    return started(space);
  });
  return current;
}

export function setSpace(space: Space | null): void {
  conversations = null;
  current = Promise.resolve(started(space));
  // Conversations sync in the background from the moment a space exists.
  if (space) void getConversations();
}

/** The conversations of this device's space, each one synced over its own room. */
export function getConversations(): Promise<Conversations | null> {
  conversations ??= getSpace().then(async (space) => {
    if (!space) return null;
    const convs = new Conversations(space, { syncFor: (conv) => {
      const sync = new SpaceSync(conv.journal, wsUrl, iceServers);
      void sync.start();
      return sync;
    } });
    await convs.load();
    const g = globalThis as { __dropMeshes?: unknown; __convs?: Conversations };
    if (g.__dropMeshes) g.__convs = convs;
    return convs;
  });
  return conversations;
}

/** Asks the browser to keep this origin's data (Safari only grants it to home-screen apps). */
export function requestPersistence(): void {
  void navigator.storage?.persist?.().catch(() => false);
}
